import {OperationTypes} from '@jimmyn/apollo-client';
import {Alert, Button, Modal} from 'antd';
import {
  CancellationReason,
  CancelPaymentInput,
  ChargeFieldsFragment,
  Maybe,
  useCancelPaymentMutation
} from 'api/generated';
import {chargeQuery} from 'api/queries';
import {Formik} from 'formik';
import {Form, FormItem, Select} from 'formik-antd';
import {FormikHelpers} from 'formik/dist/types';
import {getCancellationReasonLabels} from 'lib/chargeUtils';
import React, {useState} from 'react';

type Props = {payment?: Maybe<ChargeFieldsFragment>};

export const CancelPayment: React.FC<Props> = ({payment}) => {
  const [isOpen, setOpen] = useState(false);
  const [cancelPayment] = useCancelPaymentMutation({
    updateQuery: {query: chargeQuery, variables: {id: payment?.id}},
    operationType: OperationTypes.UPDATE
  });

  if (!payment) return null;

  const initialValues: CancelPaymentInput = {
    accountId: payment.accountId,
    paymentId: payment.id,
    cancellationReason: CancellationReason.REQUESTED_BY_CUSTOMER
  };

  const cancellationReasonLabels = getCancellationReasonLabels();

  const handleCancel = async (
    input: CancelPaymentInput,
    {setSubmitting, resetForm}: FormikHelpers<typeof initialValues>
  ) => {
    await cancelPayment({variables: {input}});
    setSubmitting(false);
    setOpen(false);
    setTimeout(resetForm, 100);
  };

  return (
    <>
      <Button onClick={() => setOpen(true)}>Cancel</Button>
      <Formik initialValues={initialValues} onSubmit={handleCancel}>
        {({submitForm, isSubmitting, resetForm}) => (
          <Modal
            title="Cancel payment"
            okText="Cancel payment"
            cancelText="Never mind"
            open={isOpen}
            confirmLoading={isSubmitting}
            onOk={submitForm}
            onCancel={() => {
              setOpen(false);
              setTimeout(resetForm, 100);
            }}>
            <Form layout="vertical">
              <Alert
                message={
                  <>
                    <b>Note:</b> if you are not using a custom integration, you should cancel
                    payments from your e-commerce admin panel to avoid an inconsistent state.
                  </>
                }
                type="info"
                showIcon
                style={{marginBottom: 20}}
              />
              <FormItem name="cancellationReason" label="Cancellation reason">
                <Select name="cancellationReason">
                  {Object.values(CancellationReason).map((reason) => (
                    <Select.Option key={reason} value={reason}>
                      {cancellationReasonLabels[reason]}
                    </Select.Option>
                  ))}
                </Select>
              </FormItem>
              <input type="submit" value="submit" hidden />
            </Form>
          </Modal>
        )}
      </Formik>
    </>
  );
};
